import { ReactNode } from "react";
import { Building2, MapPin } from "lucide-react";
import { SuggestedAdvisor } from "@/services/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/Avatar";
import { Text } from "@/components/Typography";
import { FormattedText } from "@/components/FormattedText";
import { Tooltip } from "@/components/Tooltip";
import { BriefcaseIcon } from "@/components/icons/icons";
import { PillOverflowList } from "./PillOverflowList";
import { getInitials } from "@/utils/util";
import { cn } from "@/utils/cn";

interface SuggestedAdvisorCardProps {
  advisor: SuggestedAdvisor;
  /** Extra classes applied to the outermost container element */
  className?: string;
  /**
   * "default" – used in SelectAdvisorsPage (larger responsive text, responsive avatar)
   * "compact" – used in AddNewAdvisorsModal (slightly smaller text, fixed avatar)
   */
  variant?: "default" | "compact";
  /** Optional badge rendered next to the advisor name (e.g. Invited / Accepted) */
  statusBadge?: ReactNode;
  /** Action buttons rendered at the bottom of the card */
  actions: ReactNode;
}

export const SuggestedAdvisorCard = ({
  advisor,
  className,
  variant = "default",
  statusBadge,
  actions,
}: SuggestedAdvisorCardProps) => {
  const isCompact = variant === "compact";

  return (
    <div
      className={cn(
        "flex flex-col gap-3",
        isCompact ? "p-5" : "h-full p-4 sm:gap-4 sm:p-5",
        className,
      )}
    >
      {/* Avatar + name/title/meta row */}
      <div className="flex flex-col items-start gap-3 sm:flex-row sm:gap-4">
        <Avatar
          className={cn(
            "shrink-0 border border-primary-100 dark:border-pab-dark-avatar-border bg-primary-50 dark:bg-surface-card",
            isCompact
              ? "h-16 w-16 !rounded-[12px]"
              : "h-16 w-16 !rounded-advisor-avatar sm:h-20 sm:w-20",
          )}
        >
          <AvatarImage src={advisor.profileUrl || ""} alt={advisor.name} className="grayscale" />
          <AvatarFallback
            delayMs={600}
            className={cn(
              "bg-primary-50 dark:bg-surface-card font-bold text-primary-700 dark:text-pab-dark-heading-text",
              isCompact ? "!rounded-[12px] text-[18px]" : "!rounded-advisor-avatar text-20",
            )}
          >
            {getInitials(advisor.name)}
          </AvatarFallback>
        </Avatar>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <Text
                as="p"
                className={cn(
                  "truncate font-bold text-secondary-900 dark:text-white",
                  isCompact ? "text-[18px] leading-6" : "text-20 leading-7 sm:text-28",
                )}
              >
                {advisor.name}
              </Text>
              <Text
                as="p"
                className={cn(
                  "truncate text-secondary-600 dark:text-pab-dark-subtle-text",
                  isCompact ? "mt-0.5 text-[13px]" : "mt-1 text-14",
                )}
              >
                {advisor.currentTitle}
              </Text>
            </div>
            {statusBadge}
          </div>

          <div
            className={cn(
              "flex flex-wrap items-center gap-x-3 gap-y-1 text-accent-250 dark:text-pab-dark-subtle-text",
              isCompact ? "mt-1.5" : "mt-2",
            )}
          >
            <span
              className={cn(
                "inline-flex items-center gap-1",
                isCompact ? "text-[12px]" : "text-12",
              )}
            >
              <Building2 className="h-3 w-3" />
              <span className="truncate font-inter text-[12px]">{advisor.currentCompany}</span>
            </span>
            <span
              className={cn(
                "inline-flex items-center gap-1 font-inter",
                isCompact ? "text-[12px]" : "text-12",
              )}
            >
              <BriefcaseIcon className="h-3 w-3" />
              {advisor.yearsOfExperience} years exp.
            </span>
            <span
              className={cn(
                "inline-flex items-center gap-1 font-inter",
                isCompact ? "text-[12px]" : "text-12",
              )}
            >
              <MapPin className="h-3 w-3" />
              {advisor.location}
            </span>
          </div>
        </div>
      </div>

      {(advisor.experienceContext?.length ?? 0) > 0 && (
        <PillOverflowList items={advisor.experienceContext ?? []} advisorId={advisor.id} />
      )}

      <Tooltip
        content={<p className="max-w-xs text-[12px] leading-[1.5] whitespace-pre-line"><FormattedText text={advisor.bio} /></p>}
        variant="light"
        side="top"
        showArrow={false}
      >
        <Text
          as="p"
          className={cn(
            "line-clamp-3 cursor-default font-normal text-ai-goal-back-btn-text whitespace-pre-line",
            isCompact
              ? "text-[13px] leading-[1.5]"
              : "text-14 leading-advisor-bio tracking-advisor-bio",
          )}
        >
          <FormattedText text={advisor.bio} />
        </Text>
      </Tooltip>

      <div className="mt-auto flex flex-col items-center gap-2 sm:flex-row">{actions}</div>
    </div>
  );
};
